"use client";

import { cn } from "@/src/lib/utils";
import React from "react";
import { BotIcon } from "@/components/icons";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLocale } from "@/hooks/use-locale";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslations } from "next-intl";
import {
  useUiLanguageOptions,
  useUiLanguageShortLabel,
} from "@/hooks/use-ui-language-options";
import Intercom, { show } from "@intercom/messenger-js-sdk";
import { useUserRole } from "@/hooks/useUserRole";
import { isIntercomEnabled } from "@/lib/branding";

const AppRightSidebar: React.FC = () => {
  const { currentLocale, changeLanguage } = useLocale();
  const isMobile = useIsMobile();
  const languageOptions = useUiLanguageOptions();
  const languageShortLabel = useUiLanguageShortLabel(currentLocale);

  const { isStudent } = useUserRole();
  const intercomEnabled = isIntercomEnabled();

  // Boot Intercom for admin/teacher users so that show() actually opens the messenger.
  // Students initialise it from QuizAccessibilityShell on the quiz page.
  React.useEffect(() => {
    if (!intercomEnabled) return;
    if (isStudent) return;
    Intercom({ app_id: "ep8jgj20", hide_default_launcher: true });
  }, [isStudent, intercomEnabled]);

  const openBot = React.useCallback(() => show(), []);

  if (isMobile) {
    if (isStudent) return null;
    if (!intercomEnabled) return null;

    return (
      <button
        type="button"
        className="fixed end-4 bottom-4 z-40 flex h-14 w-14 items-center justify-center rounded-2xl border border-white/80 bg-white/95 shadow-lg backdrop-blur transition-transform hover:scale-[1.02]"
        onClick={openBot}
        aria-label="Open bot"
        title="Bot"
      >
        <BotIcon title="Bot" className="scale-50 origin-center" />
      </button>
    );
  }

  return (
    <aside className={cn(isStudent ? "hidden md:flex" : "flex", "flex-col items-center justify-between bg-gradient-to-b from-white to-gray-50 border-e h-screen w-16 bg-[linear-gradient(0deg,rgba(255,255,255,.75),rgba(255,255,255,.75)),linear-gradient(270deg,rgba(219,178,158,.45)0%,rgba(219,189,172,.45)20.19%,rgba(210,195,187,.45)38.94%,rgba(175,187,207,.45)63.94%,rgba(134,149,180,.45)100%)]")}>
      <div className="flex flex-col items-center">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={cn(
                "w-16 h-16 flex items-center justify-center text-lg font-semibold select-none border-b-2 border-gray-400",
                "bg-transparent text-gray-700 font-medium text-xs border-b-2 border-white",
                "cursor-pointer"
              )}
              aria-label="Language"
              title="Language"
            >
              {languageShortLabel}
            </button>
          </DropdownMenuTrigger>

          <DropdownMenuContent side="left" align="start" className="min-w-[160px]">
            {languageOptions.map((opt) => (
              <DropdownMenuItem key={opt.locale} onSelect={() => changeLanguage(opt.locale)} className="cursor-pointer">
                <span className="me-2 inline-flex min-w-[24px] justify-center text-xs font-semibold text-muted-foreground">
                  {opt.short}
                </span>
                <span className="text-sm">{opt.label}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {intercomEnabled && (
        <div className="flex flex-col items-center">
          <button
            type="button"
            className="relative w-16 h-16 flex items-center justify-center border-t-2 border-white cursor-pointer hover:bg-gray-100 transition-colors"
            onClick={openBot}
            aria-label="Open bot"
            title="Bot"
          >
            <BotIcon title="Bot" className="scale-50 origin-center" />
          </button>
        </div>
      )}
    </aside>
  );
};

export default AppRightSidebar;
